import type { FallSourceEvidence } from '../../services/api/types.gen';

const answerLabels: Record<string, string> = {
  fall_time: 'Time of fall',
  nature_of_fall: 'Nature of fall',
  injury_level: 'Injury level',
  witness_present: 'Witnesses present',
  physician_notified_by_staff: 'Physician notified by agency staff',
  physician_notified_by_non_agency: 'Physician notified by another person',
  physician_provided_instructions: 'Physician instructions',
};
const evidenceLabels: Record<string, string> = {
  unknown: 'Recorded as unknown',
  not_documented: 'Not documented in External EMR',
  unavailable: 'Source answer unavailable',
  unmapped: 'Source answer needs review',
  conflict: 'Conflicting source answers',
};

export function FallSourceNotice({
  evidence,
}: {
  evidence: FallSourceEvidence;
}) {
  return (
    <section
      className='rounded-md border bg-muted/30 p-4 space-y-3'
      aria-label='Historical fall evidence'
    >
      <div>
        <h3 className='font-medium'>Historical External EMR evidence</h3>
        <p className='text-sm text-muted-foreground'>
          Source names describe historical involvement. They do not create Roger
          assignments or identify a Roger author.
        </p>
      </div>
      <dl className='text-sm space-y-1'>
        <div>
          <dt className='inline font-medium'>Captured: </dt>
          <dd className='inline'>{evidence.captured_at ?? 'Unavailable'}</dd>
        </div>
        <div>
          <dt className='inline font-medium'>Source workflow status: </dt>
          <dd className='inline'>{evidence.source_status ?? 'Unavailable'}</dd>
        </div>
        {evidence.source_logged_by && (
          <div>
            <dt className='inline font-medium'>Source logged by: </dt>
            <dd className='inline'>{evidence.source_logged_by}</dd>
          </div>
        )}
        {evidence.notified_physician && (
          <div>
            <dt className='inline font-medium'>Source notified physician: </dt>
            <dd className='inline'>{evidence.notified_physician}</dd>
          </div>
        )}
        {evidence.attended_staff?.length ? (
          <div>
            <dt className='inline font-medium'>Source staff in attendance: </dt>
            <dd className='inline'>{evidence.attended_staff.join('; ')}</dd>
          </div>
        ) : null}
      </dl>
      {Object.entries(evidence.field_evidence ?? {})
        .filter(([, status]) => status !== 'documented')
        .map(([field, status]) => (
          <p key={field} className='text-sm'>
            <span className='font-medium'>
              {answerLabels[field] ?? 'Source field'}:{' '}
            </span>
            {evidenceLabels[status] ?? 'Needs review'}
          </p>
        ))}
      <div className='space-y-1'>
        <h4 className='text-sm font-medium'>Source witnesses</h4>
        {(evidence.witnesses ?? []).map((witness, index) => (
          <p className='text-sm' key={index}>
            {witness.name ?? 'Name unavailable'} —{' '}
            {witness.relationship ?? 'Relationship unavailable'}
          </p>
        ))}
        {evidence.witnesses_status !== 'complete' && (
          <p className='text-sm text-muted-foreground'>
            Witness evidence is incomplete. Review the source archive.
          </p>
        )}
        {evidence.witnesses_status === 'complete' &&
          !evidence.witnesses?.length && (
            <p className='text-sm text-muted-foreground'>
              No witness entries in this capture.
            </p>
          )}
      </div>
      <div className='space-y-2'>
        <h4 className='text-sm font-medium'>Source addenda</h4>
        {(evidence.addenda ?? []).map((note, index) => (
          <div key={index} className='text-sm border-l-2 pl-3'>
            <p className='text-muted-foreground'>
              Source author: {note.source_author ?? 'Unavailable'} · Source
              date: {note.source_date ?? 'Unavailable'}
            </p>
            <p className='whitespace-pre-wrap break-words'>{note.text}</p>
          </div>
        ))}
        {evidence.addenda_status !== 'complete' && (
          <p className='text-sm text-muted-foreground'>
            Addendum evidence is incomplete. Review the source archive.
          </p>
        )}
        {evidence.addenda_status === 'complete' &&
          !evidence.addenda?.length && (
            <p className='text-sm text-muted-foreground'>
              No addenda in this capture.
            </p>
          )}
      </div>
      {evidence.gaps?.length ? (
        <p className='text-sm text-muted-foreground'>
          Some source evidence needs review. The retained archive remains
          available for comparison.
        </p>
      ) : null}
    </section>
  );
}
